import { faDisplay } from "@fortawesome/pro-light-svg-icons"
import { faChartSimple, faLocationDot, faMapLocationDot } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Box, Typography, alpha } from "@mui/material"
import { translate } from "app/language/service"
import { IRootState, NOT_SAVED_ID } from "app/model"
import { getCompactFormattedNumber } from "app/tools/number"
import { IFormInventory, IInventoryMapButtonProps, INVENTORY_MAP_VIEW_TYPES } from "form/inventory/model"
import { setInventoryMapVisibility } from "form/map/tools/inventory"
import React from "react"
import { connect } from "react-redux"
import { FlexBox } from "ui/components/FlexBox"
import { ThreeDotLoader } from "ui/components/ThreeDotLoader"
import { getInventory } from "../tools/common"

interface IFormInventoryMapProps {
  inventory: IFormInventory
}

interface IFormInventoryMapConnectProps {
  contentWidth: number
  isMapVisible: boolean
}

const InventoryButton = React.memo(
  ({ viewType, count, label, icon, isActive, isRefreshing }: IInventoryMapButtonProps) => (
    <FlexBox
      sx={{
        "position": "relative",
        "pl": "1rem",
        "pr": "1rem",
        "ml": ".1rem",
        "height": "4.5rem",
        "& .inventory-icon": {
          zIndex: 1,
          mr: ".5rem",
          color: theme => (isActive ? theme.palette.specialText : theme.palette.text.primary2)
        },
        "& .inventory-button-background, & .inventory-text, & .inventory-icon": {
          transition: "all 300ms ease"
        },
        "& .inventory-button-background": { borderLeft: theme => theme.palette.border },
        "& .inventory-button-background:last-child": { borderLeft: "none" },
        "&:hover .inventory-button-background": {
          backgroundColor: theme => `${alpha(theme.palette.text.primary2 as string, 0.55)}`
        },
        "&:hover .inventory-text, &:hover .inventory-icon": {
          color: "white"
        }
      }}
      onClick={event => {
        event.stopPropagation()
        event.preventDefault()
        setInventoryMapVisibility(true, viewType)
      }}>
      <Box
        sx={{
          position: "absolute",
          width: "100%",
          minWidth: "100%",
          maxWidth: "100%",
          height: "100%",
          minHeight: "100%",
          maxHeight: "100%",
          top: 0,
          backgroundColor: theme => (isActive ? theme.palette.primary.main : theme.palette.background.default),
          transform: "skew(-10deg)"
        }}
        className="inventory-button-background"
      />
      <FontAwesomeIcon icon={icon} className="inventory-icon" size="2x" />
      <FlexBox vertical sx={{ zIndex: 1, alignItems: "start", minWidth: "4rem" }}>
        {isRefreshing ? (
          <ThreeDotLoader sx={{ transform: "scale(.5)", mb: "0.5rem", mt: "0.5rem", ml: "1rem", height: "1rem" }} />
        ) : (
          <Typography
            sx={{
              color: theme => (isActive ? theme.palette.specialText : theme.palette.text.primary2),
              fontSize: "1.2rem",
              minWidth: "6.5rem"
            }}
            className="inventory-text">
            {getCompactFormattedNumber(count)}
          </Typography>
        )}
        <Typography
          sx={{
            color: theme => (isActive ? theme.palette.specialText : theme.palette.text.primary),
            fontSize: ".8rem"
          }}
          className="inventory-text">
          {label}
        </Typography>
      </FlexBox>
    </FlexBox>
  )
)

const FormInventoryMapFn = React.memo(
  ({
    inventory,
    contentWidth,
    isMapVisible
  }: IFormInventoryMapProps & IFormInventoryMapConnectProps): JSX.Element | null => {
    const {
      tempCampaignId: campaignId,
      mapViewType: viewType,
      totalGeoTargets,
      totalScreens,
      totalImpressions,
      isRefreshing
    } = inventory
    const isGeoTargetsDisplayed = campaignId !== NOT_SAVED_ID && totalGeoTargets > 0
    const isWidthTight = isGeoTargetsDisplayed ? contentWidth < 1250 : contentWidth < 1065
    return (
      <FlexBox
        // vertical
        sx={{
          "minWidth": isMapVisible || isWidthTight ? 0 : "20rem",
          "minHeight": "4.5rem",
          "&:hover": {
            cursor: "pointer"
          }
        }}>
        <FlexBox
          vertical
          jc="start"
          sx={{
            "width": "2.5rem",
            "minWidth": "2.5rem",
            "height": "4.5rem",
            "minHeight": "4.5rem",
            "maxHeight": "4.5rem",
            "position": "relative",
            "& > *": {
              position: "absolute",
              transition: "all 300ms ease"
            },
            "& .map-header-icon": {
              zIndex: 1,
              top: ".25rem",
              left: "1rem",
              color: theme => theme.palette.specialText
            },
            "&:hover": {
              "& .box": {
                backgroundColor: theme => theme.palette.specialText
              },
              "& .typo, & .map-header-icon": {
                color: theme => theme.palette.primary.main
              }
            }
          }}
          onClick={() => setInventoryMapVisibility(true, viewType)}>
          <Box
            className="box"
            sx={{
              width: "100%",
              height: "4.5rem",
              minHeight: "4.5rem",
              maxHeight: "4.5rem",
              right: "-.1rem",
              backgroundColor: theme => theme.palette.primary.main,
              transform: "skew(-10deg)"
            }}
          />
          <FontAwesomeIcon icon={faMapLocationDot} className="map-header-icon" />
          <Typography
            className="typo"
            sx={{
              zIndex: 1,
              color: theme => theme.palette.specialText,
              fontSize: ".75rem",
              transform: "rotate(-81deg)",
              bottom: "1.1rem"
            }}>
            {translate("label.map")}
          </Typography>
        </FlexBox>
        {!isWidthTight && (
          <FlexBox>
            {isGeoTargetsDisplayed && (
              <InventoryButton
                viewType={INVENTORY_MAP_VIEW_TYPES.GEO_TARGETS}
                count={totalGeoTargets}
                label={translate("label.geo-targets")}
                icon={faLocationDot}
                isActive={isMapVisible && viewType === INVENTORY_MAP_VIEW_TYPES.GEO_TARGETS}
                isRefreshing={false}
              />
            )}
            <InventoryButton
              viewType={INVENTORY_MAP_VIEW_TYPES.SCREENS}
              count={totalScreens}
              label={translate("label.screens")}
              icon={faDisplay}
              isActive={isMapVisible && viewType === INVENTORY_MAP_VIEW_TYPES.SCREENS}
              isRefreshing={isRefreshing}
            />
            <InventoryButton
              viewType={INVENTORY_MAP_VIEW_TYPES.IMPRESSIONS}
              count={totalImpressions}
              label={translate("label.impressions")}
              icon={faChartSimple}
              isActive={isMapVisible && viewType === INVENTORY_MAP_VIEW_TYPES.IMPRESSIONS}
              isRefreshing={isRefreshing}
            />
          </FlexBox>
        )}
      </FlexBox>
    )
  }
)

export const FormInventoryMap = connect(
  (state: IRootState): IFormInventoryMapConnectProps => ({
    contentWidth: state.ui.contentWidth,
    isMapVisible: state.app.activeContext ? getInventory(state).isMapVisible : false
  })
)(FormInventoryMapFn)
